import { useCallback, useState } from "react";

import { Box, Button, TextField, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { useSearchParams } from "react-router-dom";

import { ActivationFlow, ClientAppEvent } from "../../../../constants";
import { useAuthContext, usePasswordFieldToggle } from "../../../../hooks";
import { PasswordToggableIconEndAdornment } from "../PasswordToggableIconEndAdornment";
import {
  resolveActivationFlow,
  ResolvedActivationFlow,
  validatePassword,
} from "../utils";
import {
  CompanyMemberActivationPage,
  PasswordResetPage,
  VoluntellActivationPageRouter,
} from "./pages";

import type { ForceChangePasswordError } from "./types";

export function ForceChangePasswordContainer() {
  const [searchParams] = useSearchParams();
  const posthog = usePostHog();
  const { user, challenge, forceChangePassword, loginErrorMsg } =
    useAuthContext();
  const { passwordIsVisible, handleShowPasswordChange } =
    usePasswordFieldToggle();
  const [error, setError] = useState<null | ForceChangePasswordError>(null);
  const [newPassword, setNewPassword] = useState<string>("");
  const [voluntellMetadata, setVoluntellMetadata] = useState<
    Record<string, string>
  >({});

  const email = user?.email ?? "";
  const session = challenge?.Session ?? "";
  const shipment = searchParams.get("shipment") ?? null;
  const flow = searchParams.get("flow") ?? null;

  const isInvalidChallenge =
    !user ||
    !challenge ||
    user?.email.length === 0 ||
    challenge?.Session.length === 0;

  // The activation pages call this once per mount, after the A/B variant is
  // resolved, so it doubles as the render event the activation funnel has never
  // had — without it there is no denominator for the landing-page variants and
  // no way to separate "never reached the form" from "reached it and left".
  const handleMetadataReady = useCallback(
    (metadata: Record<string, string>) => {
      setVoluntellMetadata(metadata);
      posthog?.capture(ClientAppEvent.ACCOUNT_ACTIVATION_PAGE_VIEWED, {
        flow,
        ...metadata,
      });
    },
    [posthog, flow],
  );

  const handleOnPasswordChange = (password: string) => {
    setNewPassword(password);
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError({
        message: passwordError,
        type: "validation",
      });
    } else {
      setError(null);
    }
  };

  const handleForceChangePasswordSubmit = () => {
    forceChangePassword(email, newPassword, session, shipment);
  };

  const activateParam = searchParams.get("activate");
  const activationFlow = resolveActivationFlow(flow, activateParam);

  // Which page renders is deliberately NOT driven by activationFlow: these are
  // the pre-existing predicates, and the voluntell one is still checked first.
  // A teammate invite arrives with `activate=1` and has always been shown the
  // voluntell activation page; moving it to CompanyMemberActivationPage is a
  // product change, not an analytics fix. Only the captured event name moved.
  const rendersVoluntellActivationPage =
    flow === ActivationFlow.VOLUNTELL || Boolean(activateParam);
  const rendersCompanyMemberPage = flow === ActivationFlow.COMPANY_MEMBER;

  const getButtonText = () => {
    if (rendersVoluntellActivationPage || rendersCompanyMemberPage) {
      return "Activate Account";
    }
    return "Change Password";
  };

  const handleSubmit = () => {
    const passwordError =
      newPassword.length === 0
        ? "New password cannot be empty"
        : validatePassword(newPassword);
    if (passwordError) {
      setError({ message: passwordError, type: "validation" });
      return;
    }
    handleForceChangePasswordSubmit();
    if (posthog && activationFlow === ResolvedActivationFlow.COMPANY_MEMBER) {
      posthog.capture(ClientAppEvent.COMPANY_MEMBER_ACCOUNT_ACTIVATION, {
        flow,
        ...voluntellMetadata,
      });
    } else if (posthog && activationFlow === ResolvedActivationFlow.VOLUNTELL) {
      posthog.capture(ClientAppEvent.VOLUNTELL_ACCOUNT_ACTIVATION, {
        flow,
        ...voluntellMetadata,
      });
    } else if (posthog) {
      posthog.capture(ClientAppEvent.USER_PASSWORD_RESET, { flow });
    }
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  if (isInvalidChallenge) {
    return (
      <Box>
        <Typography textAlign="center" variant="h4" fontWeight="bold">
          Set your password to use Evotrux
        </Typography>
        <Typography color="red" align="center">
          There was an unexpected issue attempting to initialize your account.
          Please contact us.
        </Typography>
      </Box>
    );
  }

  const passwordField = (
    <TextField
      data-testid="change-password-input"
      id="change-password-input"
      label="New Password"
      type={passwordIsVisible ? "text" : "password"}
      variant="outlined"
      value={newPassword}
      onChange={(e) => handleOnPasswordChange(e.target.value)}
      InputProps={{
        endAdornment: (
          <PasswordToggableIconEndAdornment
            showPassword={passwordIsVisible}
            onIconClick={handleShowPasswordChange}
          />
        ),
      }}
      onKeyPress={submitOnEnter}
    />
  );

  const submitButton = (
    <Button
      variant="contained"
      onClick={handleSubmit}
      disabled={error?.type === "validation"}
    >
      {getButtonText()}
    </Button>
  );

  const displayError = error?.message ?? loginErrorMsg;
  const errorMessage = displayError ? (
    <Typography color="error" align="center">
      {displayError}
    </Typography>
  ) : null;

  const pageProps = {
    passwordField,
    submitButton,
    errorMessage,
  };

  if (rendersVoluntellActivationPage) {
    return (
      <VoluntellActivationPageRouter
        {...pageProps}
        onMetadataReady={handleMetadataReady}
      />
    );
  }

  if (rendersCompanyMemberPage) {
    return <CompanyMemberActivationPage {...pageProps} />;
  }

  return <PasswordResetPage {...pageProps} />;
}
